import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DownloadIcon from '@mui/icons-material/Download';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaidIcon from '@mui/icons-material/Paid';
import ScaleIcon from '@mui/icons-material/Scale';
import axios from 'axios';
import * as XLSX from 'xlsx';
import Filters from './Filters';
import { apiUrl, handleApiError } from './utils/api';
import { formatDate } from './utils/date';

const GROUP_OPTIONS = [
  { value: 'PLANT', label: 'Plant' },
  { value: 'TRUCK', label: 'Truck' },
  { value: 'DISPATCHER', label: 'Dispatcher' },
];

const money = (value) => Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const number = (value) => Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function SummaryMetric({ icon, label, value }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        minHeight: 96,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
      <Box>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h6">{value}</Typography>
      </Box>
    </Paper>
  );
}

export default function WeeklySummary() {
  const [groupType, setGroupType] = useState('PLANT');
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const totals = useMemo(() => rows.reduce(
    (acc, row) => ({
      tickets: acc.tickets + (Number(row.ticketCount) || 0),
      tons: acc.tons + (Number(row.totalTons) || 0),
      revenue: acc.revenue + (Number(row.totalRevenue) || 0),
    }),
    { tickets: 0, tons: 0, revenue: 0 },
  ), [rows]);

  const groupLabel = GROUP_OPTIONS.find((g) => g.value === groupType)?.label || 'Group';

  const loadSummary = async (filterData = filters, nextGroupType = groupType) => {
    if (!filterData?.startDate || !filterData?.endDate) {
      setError('Choose a week first.');
      return;
    }

    setFilters(filterData);
    setLoading(true);
    setError('');
    setInfo('');
    setRows([]);

    try {
      const response = await axios.get(`${apiUrl}/api/revenue-summary`, {
        params: {
          weekStart: filterData.startDate,
          weekEnd: filterData.endDate,
          groupType: nextGroupType,
        },
      });

      const lines = response.data?.summaryLines || [];
      setSummary(response.data);
      setRows(lines);
      if (lines.length === 0) {
        setInfo('No matching records found.');
      }
    } catch (err) {
      setError(handleApiError(err, 'Failed to load weekly summary'));
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const handleGroupChange = (_, value) => {
    if (!value) return;
    setGroupType(value);
    if (filters) {
      loadSummary(filters, value);
    }
  };

  const exportExcel = () => {
    const aoa = [[groupLabel, 'Tickets', 'Total Tons', 'Revenue']];
    rows.forEach((row) => {
      aoa.push([
        row.groupName || row.groupKey || 'Unknown',
        Number(row.ticketCount || 0),
        Number(row.totalTons || 0),
        Number(row.totalRevenue || 0),
      ]);
    });
    aoa.push(['Total', totals.tickets, totals.tons, totals.revenue]);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), `Weekly ${groupLabel}`);
    const tag = filters ? `${filters.startDate}_${filters.endDate}` : 'week';
    XLSX.writeFile(wb, `weekly-summary-${groupType.toLowerCase()}-${tag}.xlsx`);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5">Weekly Summary</Typography>
          {summary && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
              <Chip size="small" label={`Week ${summary.weekNo}, ${summary.year}`} />
              <Chip size="small" label={`${formatDate(summary.weekStartDate)} to ${formatDate(summary.weekEndDate)}`} />
              <Chip size="small" label={`By ${groupLabel}`} />
            </Stack>
          )}
        </Box>

        <ToggleButtonGroup
          color="primary"
          exclusive
          value={groupType}
          onChange={handleGroupChange}
          size="small"
        >
          {GROUP_OPTIONS.map((option) => (
            <ToggleButton key={option.value} value={option.value}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Filters
        ScreenName=""
        onFilterChange={(filterData) => loadSummary(filterData, groupType)}
        hideCompany
        hideTruck
      />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box sx={{ flex: 1 }}>
          <SummaryMetric icon={<AssessmentIcon />} label="Groups" value={rows.length} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <SummaryMetric icon={<LocalShippingIcon />} label="Tickets" value={totals.tickets} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <SummaryMetric icon={<ScaleIcon />} label="Total Tons" value={number(totals.tons)} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <SummaryMetric icon={<PaidIcon />} label="Revenue" value={`$${money(totals.revenue)}`} />
        </Box>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={exportExcel}
          disabled={!rows.length}
        >
          Export Excel
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {info && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}
      {loading && <CircularProgress />}

      {rows.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{groupLabel}</TableCell>
                <TableCell align="right">Tickets</TableCell>
                <TableCell align="right">Total Tons</TableCell>
                <TableCell align="right">Revenue</TableCell>
                <TableCell align="right">Revenue / Ton</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const tons = Number(row.totalTons || 0);
                const revenue = Number(row.totalRevenue || 0);
                return (
                  <TableRow key={row.groupKey || row.groupName}>
                    <TableCell>{row.groupName || row.groupKey || 'Unknown'}</TableCell>
                    <TableCell align="right">{row.ticketCount || 0}</TableCell>
                    <TableCell align="right">{number(tons)}</TableCell>
                    <TableCell align="right">${money(revenue)}</TableCell>
                    <TableCell align="right">${money(tons > 0 ? revenue / tons : 0)}</TableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell><strong>Total</strong></TableCell>
                <TableCell align="right"><strong>{totals.tickets}</strong></TableCell>
                <TableCell align="right"><strong>{number(totals.tons)}</strong></TableCell>
                <TableCell align="right"><strong>${money(totals.revenue)}</strong></TableCell>
                <TableCell align="right">
                  <strong>${money(totals.tons > 0 ? totals.revenue / totals.tons : 0)}</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
